<template>
  <div v-if="visible && bet" class="bdm-backdrop">
    <div class="bdm-overlay" @click="$emit('close')"></div>

    <div class="bdm-modal">
      <!-- Loading overlay -->
      <div v-if="loading" class="bdm-loading-overlay">
        <svg class="bdm-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="bdm-spinner__track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="bdm-spinner__arc" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <!-- Header -->
      <div class="bdm-header">
        <div class="bdm-header__left">
          <span class="bdm-header__sublabel">{{ $t('bet') }} {{ $t('code') }}:</span>
          <span class="bdm-header__code">{{ getShortCode ? bet.betCode : bet.code }}</span>
        </div>
        <div class="bdm-header__actions">
          <button v-if="bet.result === 'pending'" class="bdm-icon-btn" :title="$t('rebet')" @click="handleRebet">
            <svg class="bdm-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button v-if="!isBetCancelled && bet.status !== 'review'" class="bdm-icon-btn" :title="$t('print')" @click="handlePrint">
            <svg class="bdm-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
          <button class="bdm-icon-btn" @click="$emit('close')">
            <svg class="bdm-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="bdm-content">
        <!-- Bet summary grid -->
        <div class="bdm-summary">
          <div class="bdm-card">
            <div class="bdm-card__label">{{ $t('type') }}</div>
            <div class="bdm-card__value">
              {{ $t(bet.type) }}
              <span class="bdm-card__sub">({{ detailOdds.length }})</span>
              <span v-if="bet.betFreeze" class="bdm-freeze-badge" :class="{ 'bdm-freeze-badge--overridden': bet.betFreezeOverridden }" :title="bet.betFreezeOverridden ? $t('betFreeze.overridden') : $t('betFreeze.badge')">&#10052;</span>
            </div>
          </div>
          <div class="bdm-card">
            <div class="bdm-card__label">{{ $t('betCreationDate') }}</div>
            <div class="bdm-card__value">{{ formatDate(bet.created_at) }}</div>
          </div>
          <div class="bdm-card">
            <div class="bdm-card__label">{{ $t('amount') }}</div>
            <div class="bdm-card__value">{{ bet.currencySymbol }} {{ formatAmount(bet.amount_real) }}</div>
          </div>
          <div class="bdm-card">
            <div class="bdm-card__label">{{ $t('potential') }} {{ $t('winning') }}</div>
            <div class="bdm-card__value bdm-card__value--primary">
              <s v-if="betFreezeOriginalWinning(bet)" class="bdm-freeze-original" :title="originalWinningLabel(bet)">{{ betFreezeOriginalWinning(bet) }}</s>
              {{ getWinning(bet) }}
            </div>
          </div>
          <!-- Multiple bonus that applies today (a void or frozen leg can lower the tier), placement one struck -->
          <div v-if="bonusDisplay(bet) && bonusDisplay(bet).shown" class="bdm-card bdm-card--wide">
            <div class="bdm-card__label">{{ $t('bonus') }}</div>
            <div class="bdm-card__value">
              <s v-if="bonusDisplay(bet).original" class="bdm-freeze-original" :title="originalWinningLabel(bet)">{{ bonusDisplay(bet).original }}</s>
              {{ bonusDisplay(bet).current }}
            </div>
          </div>
        </div>

        <!-- betFreeze available: how many selections can still be frozen -->
        <div v-if="betFreezeAvailableCount > 0" class="bdm-freeze-available">
          <span class="bdm-freeze-available__icon">&#10052;</span>
          <span>{{ $tc('betFreeze.availableCount', betFreezeAvailableCount, {n: betFreezeAvailableCount}) }}</span>
        </div>

        <!-- Odds list -->
        <div class="bdm-odds-list">
          <template v-for="group in groupedDetailOdds">
            <!-- BET BUILDER group -->
            <div v-if="group.is_bet_builder" :key="'bb-' + group.key" class="bdm-odd-card cia-bb-group">
              <div class="cia-bb-label">
                <svg xmlns="http://www.w3.org/2000/svg" width="35" height="14" viewBox="0 0 35 14">
                  <rect width="30" height="14" rx="3" fill="var(--color-primary-light, #f90)"></rect>
                  <text x="15" y="10" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" font-weight="900" fill="#000">SGP</text>
                </svg>
                combo
              </div>
              <div class="cia-bb-event-row">
                <span class="cia-bb-event-name">{{ group.event_description }}</span>
                <span class="cia-bb-odd">{{ getBBOdd(group) | odds_type_filter }}</span>
              </div>
              <div class="cia-bb-timeline">
                <div
                  v-for="(odd, idx) in group.odds"
                  :key="odd.unique_id || idx"
                  class="cia-bb-row"
                  :class="{ 'cia-bb-row--last': idx === group.odds.length - 1 }"
                >
                  <div class="cia-bb-dot"></div>
                  <div class="cia-bb-content">
                    <span class="cia-bb-outcome">{{ getOutcomeName(odd) }} {{formatLineDisplay(odd.line, odd.id_market)}} </span>
                    <span class="cia-bb-market">{{ odd.market_alias || odd.market_name }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Regular odd -->
            <div v-else :key="group.key" class="bdm-odd-card">
              <div class="bdm-odd-card__inner">
                <div class="bdm-odd-card__left">
                  <div class="bdm-odd-card__date">{{ formatOddDate(group.odds[0].time) }}</div>
                  <div class="bdm-odd-card__tournament">
                    {{ group.odds[0].category && group.odds[0].category.name }}
                    <span class="bdm-separator">›</span>
                    {{ group.odds[0].tournament && group.odds[0].tournament.name }}
                  </div>
                  <div class="bdm-odd-card__event">
                    {{ group.odds[0].event_description || (group.odds[0].tournament && group.odds[0].tournament.name) }}
                  </div>
                  <div class="bdm-odd-card__outcome">
                    <span class="bdm-odd-card__outcome-name">{{ getOutcomeName(group.odds[0]) }} {{formatLineDisplay(group.odds[0].line, group.odds[0].id_market)}}</span>
                    <span class="bdm-odd-card__market">
                      ({{ group.odds[0].market_alias || group.odds[0].market_name }})
                    </span>
                  </div>
                  <div
                    v-if="bet.status !== 'finished' && group.odds[0].show_time && !group.odds[0].match_result"
                    class="bdm-odd-card__livescore"
                  >
                    {{ group.odds[0].result_actual_home }} - {{ group.odds[0].result_actual_away }}
                    <span v-if="group.odds[0].time_actual != null && group.odds[0].time_actual >= 0">
                      ({{ formatTimeActual(group.odds[0].time_actual) }}')
                    </span>
                  </div>
                </div>
                <div class="bdm-odd-card__right">
                  <div v-if="group.odds[0].match_result" class="bdm-odd-card__result">
                    [{{ group.odds[0].match_result }}]
                  </div>
                  <div v-else-if="isLegFrozen(group.odds[0])" class="bdm-odd-card__odd bdm-odd-card__odd--frozen" :title="$t('betFreeze.frozenLeg')">
                    <s class="bdm-odd-card__odd-placed">{{ group.odds[0].outcome_odd_value | odds_type_filter }}</s>
                    &#10052; {{ formatFreezeOdd(group.odds[0].betFreeze.frozenOdd) }}
                  </div>
                  <div v-else class="bdm-odd-card__odd" :title="isLegFreezeOverridden(group.odds[0]) ? legFreezeOverriddenLabel(group.odds[0]) : null">
                    <span v-if="isLegFreezeOverridden(group.odds[0])" class="bdm-odd-card__freeze-overridden">&#10052;</span>
                    {{ group.odds[0].outcome_odd_value | odds_type_filter }}
                  </div>
                  <BetStatusIcon :status="getOddStatus(group.odds[0])" :halfWon="group.odds[0].half_won" :halfLost="group.odds[0].half_lost" />
                </div>
              </div>

              <!-- betFreeze: action on an open leg (the server decides what is freezable) -->
              <div v-for="leg in betFreezeLegList(group.odds[0])" :key="'freeze-' + group.key" class="bdm-freeze" @click.stop>
                <template v-if="betFreezeConfirm.legUniqueId !== String(group.odds[0].unique_id)">
                  <button v-if="leg.freezable" class="bdm-freeze-bar" @click="openBetFreezeConfirm(group.odds[0])">
                    <span class="bdm-freeze-bar__action">&#10052; {{ $t('betFreeze.cta') }} <span class="bdm-freeze-bar__chevron">&rsaquo;</span></span>
                    <span class="bdm-freeze-bar__price">
                      <span v-if="betFreezeCost(bet, leg)" class="bdm-freeze-bar__cost">
                        <span class="bdm-freeze-bar__cost-amount">&minus;{{ bet.currencySymbol }} {{ betFreezeCost(bet, leg).amount }}</span>
                        (&minus;{{ betFreezeCost(bet, leg).percent }}%)
                      </span>
                      <span class="bdm-freeze-bar__odds">
                        <s class="bdm-freeze-bar__placed">{{ leg.initialOdd | odds_type_filter }}</s>
                        <span class="bdm-freeze-bar__arrow">&rarr;</span>
                        <b class="bdm-freeze-bar__frozen">{{ formatFreezeOdd(leg.frozenOdd) }}</b>
                      </span>
                    </span>
                  </button>
                  <button v-else class="bdm-freeze-bar bdm-freeze-bar--disabled" disabled :title="betFreezeReasonText(leg.reason)">
                    <span class="bdm-freeze-bar__action">&#10052; {{ $t('betFreeze.action') }}</span>
                    <span class="bdm-freeze-bar__reason">{{ betFreezeReasonText(leg.reason) }}</span>
                  </button>
                </template>
                <div v-else class="bdm-freeze-panel">
                  <div class="bdm-freeze-panel__title">&#10052; {{ $t('betFreeze.confirmTitle') }}</div>
                  <div class="bdm-freeze-panel__row">
                    <span>{{ $t('betFreeze.placedOdd') }}</span>
                    <span>{{ leg.initialOdd | odds_type_filter }}</span>
                  </div>
                  <div class="bdm-freeze-panel__row">
                    <span>{{ $t('betFreeze.currentOdd') }}</span>
                    <span>{{ leg.currentOdd | odds_type_filter }}</span>
                  </div>
                  <div class="bdm-freeze-panel__row bdm-freeze-panel__row--accent">
                    <span>{{ $t('betFreeze.frozenOdd') }}</span>
                    <span>{{ formatFreezeOdd(leg.frozenOdd) }}</span>
                  </div>
                  <template v-if="betFreezeFairInfo(leg)">
                    <div class="bdm-freeze-panel__row" :title="$t('betFreeze.fairValueHelp')">
                      <span>{{ $t('betFreeze.fairValue') }} <span class="bdm-freeze-help">&#9432;</span></span>
                      <span>{{ formatFreezeOdd(betFreezeFairInfo(leg).fair) }}</span>
                    </div>
                    <div class="bdm-freeze-panel__fair">{{ $t('betFreeze.fairValueOffer', betFreezeFairInfo(leg)) }}</div>
                  </template>
                  <div v-if="leg.estimatedPotentialWinning != null" class="bdm-freeze-panel__row">
                    <span>{{ $t('betFreeze.newPotentialWinning') }}</span>
                    <span>
                      <s v-if="betFreezeCurrentWinning(bet) !== null" class="bdm-freeze-original">{{ betFreezeCurrentWinning(bet) }}</s>
                      &asymp; {{ bet.currencySymbol }} {{ parseFloat(leg.estimatedPotentialWinning).toFixed(2) }}
                    </span>
                  </div>
                  <template v-if="betFreezeCost(bet, leg)">
                    <div class="bdm-freeze-panel__row bdm-freeze-panel__row--cost">
                      <span>{{ $t('betFreeze.cost') }}</span>
                      <span>&minus;{{ bet.currencySymbol }} {{ betFreezeCost(bet, leg).amount }} (&minus;{{ betFreezeCost(bet, leg).percent }}%)</span>
                    </div>
                    <div class="bdm-freeze-panel__benefit">{{ $t('betFreeze.benefit') }}</div>
                  </template>
                  <div class="bdm-freeze-panel__validity">
                    <template v-if="betFreezeConfirm.busy && leg.isLive">
                      <span class="bdm-freeze-spin"></span>{{ $t('betFreeze.verifying') }}
                    </template>
                    <template v-else-if="betFreezeConfirm.refreshing">
                      <span class="bdm-freeze-spin"></span>{{ $t('betFreeze.refreshingPrice') }}
                    </template>
                    <template v-else-if="betFreezeSecondsLeft(group.odds[0]) !== null">
                      {{ $t('betFreeze.validFor', { seconds: betFreezeSecondsLeft(group.odds[0]) }) }}
                    </template>
                  </div>
                  <div class="bdm-freeze-panel__note">{{ $t('betFreeze.irreversible') }}</div>
                  <div v-if="betFreezeConfirm.message" class="bdm-freeze-panel__message" :class="`bdm-freeze-panel__message--${betFreezeConfirm.messageType}`">
                    {{ betFreezeConfirm.message }}
                  </div>
                  <div class="bdm-freeze-panel__actions">
                    <button class="bdm-freeze-btn bdm-freeze-btn--ghost" :disabled="betFreezeConfirm.busy" @click="closeBetFreezeConfirm">{{ $t('betFreeze.cancel') }}</button>
                    <button class="bdm-freeze-btn" :disabled="betFreezeConfirm.busy || betFreezeConfirm.refreshing" @click="confirmBetFreeze(group.odds[0])">
                      <span v-if="betFreezeConfirm.busy" class="bdm-freeze-spin"></span>{{ $t('betFreeze.confirm') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div v-if="betFreezeNotice" class="bdm-freeze-notice" :class="`bdm-freeze-notice--${betFreezeNotice.type}`">
          &#10052; {{ betFreezeNotice.text }}
        </div>

        <!-- Status and winning -->
        <div class="bdm-result-card">
          <div class="bdm-result-card__row">
            <span class="bdm-result-card__label">{{ $t('bets.status') }}</span>
            <div class="bdm-result-card__status">
              <BetStatusIcon :status="bet.result" />
              <span class="bdm-result-card__status-text" :class="getResultClass(bet.result)">
                {{ $t(bet.result) }}
              </span>
              <span v-if="bet.cashout" class="bdm-result-card__cashout-badge">(cashout)</span>
            </div>
          </div>
          <div class="bdm-result-card__row">
            <span class="bdm-result-card__label">{{ $t('winning') }}</span>
            <span class="bdm-result-card__winning">
              {{ bet.winning_real ? `${bet.currencySymbol} ${formatAmount(bet.winning_real)}` : `${bet.currencySymbol} 0.00` }}
            </span>
          </div>
        </div>

        <!-- Cashout button -->
        <div v-if="(cashout > 0 || cashoutPrematch > 0) && !bet.cashout && !hasBetFreeze" class="bdm-cashout">
          <button class="bdm-cashout__btn" @click="handleCashoutClick">
            Cashout: {{ bet.currencySymbol }}
            {{ cashout > 0 ? formatAmount(cashout) : formatAmount(cashoutPrematch) }}
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="bdm-footer">
        <button class="bdm-close-btn" @click="$emit('close')">
          {{ $t('close') }}
        </button>
      </div>
    </div>

    <CashoutDialog
      :visible="showCashoutDialog"
      :cashoutValue="cashoutDisplayValue"
      :potentialWin="potentialWinDisplay"
      :loading="cashoutLoading"
      @close="showCashoutDialog = false"
      @confirm="handleCashoutConfirm"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BetStatusIcon from './BetStatusIcon.vue';
import CashoutDialog from './CashoutDialog.vue';
import {MARKET_ASIAN_HANDICAP} from '@/constants/marketConstants';
import betFreezeDetailMixin from '@/mixins/betFreezeDetailMixin';


export default {
  name: 'BetDetailModal',
  components: { BetStatusIcon, CashoutDialog },
  // betFreeze logic, shared with the classic MyBets.
  mixins: [betFreezeDetailMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    bet: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    cashout: {
      type: Number,
      default: 0
    },
    cashoutPrematch: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      showCashoutDialog: false,
      cashoutLoading: false
    };
  },
  computed: {
    ...mapGetters({
      getShortCode: 'settings/getShortCode',
      getPlatformSettings: 'settings/getPlatformSettings',
      getOddsType: 'odds/getOddsType',
      getDetailMyBets: 'user/getDetailMyBets',
      getType: 'user/getType',
      getId: 'user/getId',
      getMarketConfig: 'settings/getMarketConfig',
      getMyBets: 'user/getMyBets',
      getCashier: 'user/getCashier',
      getProfileType: 'user/getProfileType',
      getTerminal: 'user/getTerminal',
      getEnableAgencyCashout: 'settings/getEnableAgencyCashout',
      getEnableCashierCashout: 'settings/getEnableCashierCashout',
      getEnableTerminalCashout: 'settings/getEnableTerminalCashout',
    }),
    // betFreezeDetailMixin works on `betToShow`.
    betToShow() {
      return this.bet;
    },
    // Same audience as the cashout (IllinoisMyBets): betFreeze is asked for it only.
    isPlayerEligibleForCashout() {
      const profileType = this.getProfileType;
      const isAgency   = this.getType != null && this.getType == 4;
      const isCashier  = profileType === 'CASHIER' || !!this.getCashier;
      const isTerminal = profileType === 'TERMINAL' || !!this.getTerminal;
      if (isAgency   && this.getEnableAgencyCashout)   return true;
      if (isCashier  && this.getEnableCashierCashout)  return true;
      if (isTerminal && this.getEnableTerminalCashout) return true;
      return !isAgency && !isCashier && !isTerminal;
    },
    // No cashout on a bet with a betFreeze applied (enforced server-side too).
    hasBetFreeze() {
      return Boolean(this.bet) && (this.bet.betFreeze === true || (this.bet.betFreezeCount || 0) > 0);
    },
    // Changes when the modal opens or shows another bet: fresh betFreeze quotes.
    betFreezeDetailKey() {
      return this.visible && this.bet ? this.bet.code : null;
    },
    isBetCancelled() {
      return this.bet && this.bet.result === 'cancelled';
    },
    detailOdds() {
      const detail = this.getDetailMyBets;
      if (detail && detail.odds && Array.isArray(detail.odds)) {
        return detail.odds;
      }
      return (this.bet && this.bet.odds) || [];
    },
    cashoutDisplayValue() {
      if (!this.bet) return '';
      const value = this.cashout > 0 ? this.cashout : this.cashoutPrematch;
      return `${this.bet.currencySymbol} ${this.formatAmount(value)}`;
    },
    potentialWinDisplay() {
      if (!this.bet) return '';
      return this.getWinning(this.bet);
    },
    groupedDetailOdds() {
      const map = new Map();
      this.detailOdds.forEach((odd, idx) => {
        const key = odd.bet_builder_id || odd.unique_id || `idx-${idx}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            event_description: odd.event_description,
            is_bet_builder: !!odd.bet_builder_id,
            odds: [],
          });
        }
        map.get(key).odds.push(odd);
      });
      return Array.from(map.values());
    }
  },
  watch: {
    // Same bet re-shown after a freeze (same code): nothing to reset, the
    // mixin already refreshed the quotes and keeps its notice on screen.
    betFreezeDetailKey(key) {
      if (key) this._onBetFreezeDetailOpened(this.bet);
      else this.closeBetFreezeConfirm();
    },
  },
  methods: {
    ...mapActions({
      resetCart: 'cart/resetCart',
      setOddsInCart: 'cart/setOddsInCart',
      setCreatedAtCart: 'cart/setCreatedAtCart',
      refreshCart: 'cart/refreshCart',
      sentCashout: 'odds/sentCashout',
      fetchMyBets: 'user/fetchMyBets',
      setDetailMyBet: 'user/setDetailMyBet'
    }),
    stripScoreState(str, market) {
      if (!str) return str;

      let st = String(str).replace(/\s*\(\d+-\d+\)\s*$/, '').trim();

      if (MARKET_ASIAN_HANDICAP.includes(market) && !st.startsWith('-'))
        return '+' + st;

      return st
    },
    formatLineDisplay(line, market) {
      console.log("line", line)
      console.log("market", market)
      if (!line) return '';
      let str = this.stripScoreState(String(line).trim());

      if (this.isHandicapMarket(market)) {
        if (/^[0-9]/.test(str) && parseFloat(str) !== 0) {
          return '+' + str;
        }
      }

      if (MARKET_ASIAN_HANDICAP.includes(market) && !str.startsWith('-'))
        return '+' + str;

      return str;
    },
    isHandicapMarket(market) {
      if (!market) return false;
      const id = parseInt( market, 10);
      return this.getMarketConfig.handicapIds.includes(id);
    },
    getOutcomeName(odd) {
      console.log(odd);
      const name = odd.outcome_name;
      if (!name) return odd.outcome_alias || '';
      if (name === 'X') return odd.outcome_alias || odd.outcome_name;
      const lower = name.toLowerCase();
      const c = odd.competitors;
      if (lower === 'local' && c && c[0]) return c[0].name;
      if (lower === 'visitante' && c && c[1]) return c[1].name;
      if ((name === '1' || name === '2') && c) {
        const found = c.find(t => t.position === name);
        if (found) return found.name;
      }
      return odd.outcome_alias || name;
    },
    getBBOdd(group) {
      const main = group.odds.find(o => parseFloat(o.outcome_odd_value) !== 1);
      return main ? main.outcome_odd_value : (group.odds[0] ? group.odds[0].outcome_odd_value : 0);
    },
    removeParentheses(str) {
      if (!str) return '';
      return String(str).replace(/\(.*?\)/g, '').trim();
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    formatOddDate(time) {
      const date = new Date(time);
      return `${date.toLocaleDateString('en-GB', { month: 'numeric', day: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    },
    formatAmount(amount) {
      return parseFloat(String(amount)).toFixed(2);
    },
    formatTimeActual(time) {
      return parseFloat(String(time)).toFixed(0);
    },
    // --- betFreeze (betFreezeDetailMixin hooks) ------------------------------
    formatBetFreezeMoney(bet, amount) {
      return `${bet.currencySymbol} ${amount.toFixed(2)}`;
    },
    // Reload the bets and re-open the detail on the fresh bet, through the
    // same path as a normal open (IllinoisMyBets.openDetail: detail store
    // first, then the event; TemplateAmerican adds the live scores). Setting
    // the store first matters: TemplateAmerican syncs the modal's bet from
    // it, a stale store would flash the old bet (and re-ask its quotes).
    async _reloadBetsAfterBetFreeze(code) {
      await this.fetchMyBets({ all: false });
      const fresh = (this.getMyBets || []).find(b => b && b.code === code);
      if (fresh && this.visible) {
        this.setDetailMyBet(fresh);
        this.$root.$emit('openBetDetailModal', fresh);
      }
    },
    // The leg's betFreeze state as a 0/1-item list: lets the template name it.
    betFreezeLegList(odd) {
      const leg = this.betFreezeLegState(odd);
      return leg ? [leg] : [];
    },
    getWinning(bet) {
      switch (bet.type) {
        case 'single':
        case 'multiple': {
          // After a betFreeze the placement-time figure is obsolete.
          const potential = this._pendingPotential(bet);
          return `${bet.currencySymbol} ${this.formatAmount(Number.isFinite(potential) ? potential : 0)}`;
        }
        case 'integral':
          return `${bet.currencySymbol} ${this.formatAmount(bet._integral_detail.winning_total_max)}`;
        case 'system':
          return `${bet.currencySymbol} ${this.formatAmount(bet._system_detail.winning_all_total)}`;
        default:
          return `${bet.currencySymbol} 0.00`;
      }
    },
    getOddStatus(odd) {
      if (this.bet.cashout && !odd.result) return 'void';
      if (this.bet.cashout && odd.result) return 'win';
      if (odd.result === undefined) return 'pending';
      if (odd.void) return 'void';
      if (odd.cancelled) return 'cancelled';
      if (odd.result) return 'win';
      return 'lose';
    },
    getResultClass(result) {
      const map = { win: 'status--win', lose: 'status--lose', void: 'status--void', cancelled: 'status--cancelled' };
      return map[result] || 'status--pending';
    },
    async handleRebet() {
      if (!this.bet) return;
      this.$emit('close');
      const now = new Date();
      await this.resetCart();
      await this.setOddsInCart(this.bet.odds);
      await this.setCreatedAtCart(now.getTime());
      await this.refreshCart();
      this.$root.$emit('sectionCart');
    },
    handlePrint() {
      if (!this.bet || this.isBetCancelled || this.bet.status === 'review') return;
      const code = this.getShortCode ? this.bet.betCode : this.bet.code;
      let printUrl = `${this.getPlatformSettings.print_endpoint}${code}&type_odd=${this.getOddsType}`;
      if (this.getShortCode) {
        printUrl += `&login=${this.getType}&ancestors=${this.getId}`;
      }
      window.open(printUrl, '_blank', 'height=600,width=350');
    },
    handleCashoutClick() {
      this.showCashoutDialog = true;
    },
    async handleCashoutConfirm() {
      if (!this.bet) return;
      this.cashoutLoading = true;
      try {
        const cashoutValue = this.cashout > 0
          ? parseFloat(String(this.cashout)).toFixed(2)
          : this.cashoutPrematch > 0
          ? parseFloat(String(this.cashoutPrematch)).toFixed(2)
          : '';
        const result = await this.sentCashout({ code: this.bet.code, cashout: cashoutValue });
        await this.fetchMyBets({ all: false });
        this.showCashoutDialog = false;
        this.$emit('close');
        if (result && result.updated) {
          this.$emit('cashout-success');
        } else {
          this.$emit('cashout-error');
        }
      } catch (error) {
        console.error('Cashout error:', error);
        this.$emit('cashout-error');
      } finally {
        this.cashoutLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.bdm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1003;
  display: flex;
  align-items: center;
  justify-content: center;
  bottom: 60px;
}

.bdm-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
}

.bdm-modal {
  position: relative;
  background-color: var(--mybets-card-bg, #2a2a2a);
  width: 100%;
  height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  border: 1px solid var(--mybets-border, rgba(255, 255, 255, 0.1));
  overflow: hidden;
}

@media (min-width: 768px) {
  .bdm-modal {
    border-radius: 8px;
    width: 448px;
    height: auto;
    max-height: 90vh;
    margin: 0 16px;
  }
}

/* --- Loading overlay --- */
.bdm-loading-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.bdm-spinner {
  width: 32px;
  height: 32px;
  color: var(--mybets-primary, #e92742);
  animation: bdm-spin 1s linear infinite;
}

.bdm-spinner__track { opacity: 0.25; }
.bdm-spinner__arc { opacity: 0.75; }

@keyframes bdm-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* --- Header --- */
.bdm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--mybets-border, rgba(255, 255, 255, 0.1));
  background-color: rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
}

.bdm-header__left {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.bdm-header__sublabel {
  font-size: var(--font-size-2xl);
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
  text-transform: uppercase;
}

.bdm-header__code {
  font-size: var(--font-size-7xl);
  font-weight: 700;
  color: var(--mybets-primary, #e92742);
  font-family: monospace;
}

.bdm-header__actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.bdm-icon-btn {
  padding: 8px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.bdm-icon-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.bdm-icon {
  width: 20px;
  height: 20px;
  color: var(--mybets-text, #ffffff);
}

/* --- Content --- */
.bdm-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 16px;
}

/* --- Summary --- */
.bdm-summary {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.bdm-card {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
  border: 1px solid var(--mybets-border, rgba(255, 255, 255, 0.1));
}

.bdm-card--wide { grid-column: 1 / -1; }

.bdm-card__label {
  font-size: var(--font-size-2xl);
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
  margin-bottom: 4px;
}

.bdm-card__value {
  font-size: var(--font-size-4xl);
  font-weight: 600;
  color: var(--mybets-text, #ffffff);
  text-transform: capitalize;
}

.bdm-card__value--primary {
  color: var(--mybets-primary, #e92742);
}

.bdm-card__sub {
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
}

/* --- Odds list --- */
.bdm-odds-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.bdm-odd-card {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
  border: 1px solid var(--mybets-border, rgba(255, 255, 255, 0.1));
}

.bdm-odd-card__inner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.bdm-odd-card__left {
  flex: 1;
  min-width: 0;
}

.bdm-odd-card__date {
  font-size: var(--font-size-2xl);
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
  margin-bottom: 4px;
}

.bdm-odd-card__tournament {
  font-size: var(--font-size-2xl);
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
  margin-bottom: 4px;
}

.bdm-separator {
  margin: 0 4px;
}

.bdm-odd-card__event {
  font-size: var(--font-size-4xl);
  font-weight: 500;
  color: var(--mybets-text, #ffffff);
  margin-bottom: 4px;
}

.bdm-odd-card__outcome {
  font-size: var(--font-size-2xl);
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
}

.bdm-odd-card__outcome-name {
  color: var(--mybets-primary, #e92742);
  font-weight: 500;
}

.bdm-odd-card__market {
  margin-left: 4px;
}

.bdm-odd-card__livescore {
  font-size: var(--font-size-2xl);
  color: #eab308;
  margin-top: 4px;
}

.bdm-odd-card__right {
  text-align: right;
  margin-left: 8px;
}

.bdm-odd-card__result {
  font-size: var(--font-size-4xl);
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
  margin-bottom: 4px;
}

.bdm-odd-card__odd {
  font-size: var(--font-size-4xl);
  font-weight: 700;
  color: var(--mybets-primary, #e92742);
  margin-bottom: 4px;
}

/* --- Result card --- */
.bdm-result-card {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
  border: 1px solid var(--mybets-border, rgba(255, 255, 255, 0.1));
  margin-bottom: 16px;
}

.bdm-result-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.bdm-result-card__row + .bdm-result-card__row {
  margin-top: 8px;
}

.bdm-result-card__label {
  font-size: var(--font-size-4xl);
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
}

.bdm-result-card__status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bdm-result-card__status-text {
  font-size: var(--font-size-4xl);
  font-weight: 500;
  text-transform: capitalize;
}

.status--win { color: #22c55e; }
.status--lose { color: #ef4444; }
.status--void { color: #3b82f6; }
.status--cancelled { color: #eab308; }
.status--pending { color: #eab308; }

.bdm-result-card__cashout-badge {
  font-size: var(--font-size-2xl);
  color: #22c55e;
}

.bdm-result-card__winning {
  font-size: var(--font-size-4xl);
  font-weight: 700;
  color: var(--mybets-text, #ffffff);
}

/* --- Cashout --- */
.bdm-cashout {
  margin-bottom: 8px;
}

.bdm-cashout__btn {
  width: 100%;
  padding: 12px 16px;
  border-radius: 6px;
  background-color: #16a34a;
  color: #ffffff;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-size: var(--font-size-4xl);
}

.bdm-cashout__btn:hover {
  background-color: #15803d;
}

/* --- Footer --- */
.bdm-footer {
  padding: 16px;
  border-top: 1px solid var(--mybets-border, rgba(255, 255, 255, 0.1));
  flex-shrink: 0;
}

.bdm-close-btn {
  width: 100%;
  height: 52px;
  border-radius: 6px;
  background-color: var(--mybets-primary, #e92742);
  color: #ffffff;
  font-weight: 500;
  font-size: var(--font-size-4xl);
  text-transform: capitalize;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.bdm-close-btn:hover {
  opacity: 0.9;
}

/* ── BET BUILDER GROUP (dark modal context) ── */
.cia-bb-group {
  padding: 10px 12px 10px;
}

.cia-bb-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-xl);
  font-weight: 800;
  font-style: italic;
  text-transform: uppercase;
  color: var(--mybets-text, #ffffff);
  letter-spacing: 0.06em;
  margin-bottom: 8px;
}

.cia-bb-event-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.cia-bb-event-name {
  flex: 1;
  font-size: var(--font-size-3xl);
  font-weight: 700;
  color: var(--mybets-text, #ffffff);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cia-bb-odd {
  font-size: var(--font-size-4xl);
  font-weight: 800;
  color: var(--mybets-primary, #e92742);
  flex-shrink: 0;
}

.cia-bb-timeline {
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cia-bb-row {
  position: relative;
  display: flex;
  align-items: flex-start;
}

.cia-bb-row:not(.cia-bb-row--last)::before {
  content: "";
  position: absolute;
  left: -12px;
  top: 16px;
  bottom: -10px;
  width: 1px;
  background: rgba(0,0,0,.18);
}

.cia-bb-dot {
  position: absolute;
  left: -16px;
  top: 5px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1.5px solid var(--mybets-primary, #e92742);
  background: transparent;
}

.cia-bb-content {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.cia-bb-outcome {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  color: var(--mybets-primary, #e92742);
  word-break: break-word;
}

.cia-bb-market {
  font-size: var(--font-size-2xl);
  font-weight: 600;
  color: var(--mybets-text-muted, rgba(255, 255, 255, 0.5));
}

/* ── betFreeze ── accent #0ea5e9 (readable on the light and dark themes); text
   and surfaces follow the --mybets-* theme variables like the rest of the modal. */
.bdm-freeze-badge { margin-left: 6px; color: #0ea5e9; font-size: 0.95em; line-height: 1; text-transform: none; }
.bdm-freeze-original { opacity: 0.55; font-weight: 400; font-size: 0.85em; margin-right: 6px; color: inherit; }
.bdm-freeze-available { display: flex; align-items: center; gap: 8px; margin: 0 0 12px; padding: 8px 12px; border-radius: 6px; border: 1px solid rgba(14, 165, 233, 0.45); background: rgba(14, 165, 233, 0.1); color: var(--mybets-text, #ffffff); font-size: var(--font-size-3xl); font-weight: 600; }
.bdm-freeze-available__icon { color: #0ea5e9; font-size: 1.1em; }
.bdm-odd-card__odd--frozen { color: #0ea5e9; white-space: nowrap; }
.bdm-odd-card__odd-placed { opacity: 0.6; font-weight: 400; margin-right: 4px; color: var(--mybets-text, #ffffff); }
.bdm-freeze { margin-top: 10px; padding-top: 10px; border-top: 1px dashed rgba(14, 165, 233, 0.35); }
.bdm-freeze-bar { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%; border: 1px solid rgba(14, 165, 233, 0.6); border-radius: 6px; padding: 8px 12px; background: linear-gradient(90deg, rgba(14, 165, 233, 0.18), rgba(14, 165, 233, 0.04)); color: var(--mybets-text, #ffffff); font-size: var(--font-size-3xl); font-weight: 700; cursor: pointer; transition: background 0.15s ease, transform 0.1s ease; }
.bdm-freeze-bar:hover { background: linear-gradient(90deg, rgba(14, 165, 233, 0.3), rgba(14, 165, 233, 0.1)); }
.bdm-freeze-bar:active { transform: scale(0.99); }
.bdm-freeze-bar__action { display: inline-flex; align-items: center; gap: 6px; color: #0ea5e9; letter-spacing: 0.3px; white-space: nowrap; }
.bdm-freeze-bar__chevron { font-size: 1.1em; line-height: 1; opacity: 0.85; }
.bdm-freeze-bar__price { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.bdm-freeze-bar__cost { color: #d97706; font-weight: 600; font-size: 0.9em; white-space: nowrap; }
.bdm-freeze-bar__odds { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.bdm-freeze-bar__placed { opacity: 0.6; font-weight: 400; }
.bdm-freeze-bar__arrow { opacity: 0.7; }
.bdm-freeze-bar__frozen { color: #0ea5e9; font-size: 1.1em; }
.bdm-freeze-bar--disabled { cursor: default; border-color: rgba(148, 163, 184, 0.5); background: rgba(148, 163, 184, 0.1); }
.bdm-freeze-bar--disabled .bdm-freeze-bar__action { color: #94a3b8; }
.bdm-freeze-bar__reason { font-weight: 400; color: var(--mybets-text-muted, #94a3b8); font-size: 0.9em; text-align: right; }
/* Narrow modal: percentage only, the amount is in the confirm panel */
@media (max-width: 420px) { .bdm-freeze-bar__cost-amount { display: none; } }
.bdm-freeze-panel { border: 1px solid #0ea5e9; border-radius: 6px; padding: 10px 12px; font-size: var(--font-size-3xl); color: var(--mybets-text, #ffffff); }
.bdm-freeze-panel__title { font-weight: 800; color: #0ea5e9; margin-bottom: 6px; }
.bdm-freeze-panel__row { display: flex; justify-content: space-between; gap: 8px; padding: 2px 0; }
.bdm-freeze-panel__row--accent { font-weight: 800; color: #0ea5e9; }
.bdm-freeze-panel__row--cost span:last-child { color: #d97706; font-weight: 600; }
.bdm-freeze-panel__fair { font-size: 0.9em; color: #16a34a; margin: -2px 0 6px; text-align: right; }
.bdm-freeze-help { opacity: 0.6; font-size: 0.8em; cursor: help; }
.bdm-freeze-panel__benefit { font-size: 0.9em; opacity: 0.8; margin: 2px 0 6px; }
.bdm-freeze-panel__validity { margin-top: 6px; font-size: 0.9em; font-weight: 700; color: #0ea5e9; display: flex; align-items: center; gap: 6px; min-height: 16px; }
.bdm-freeze-panel__note { margin-top: 6px; opacity: 0.75; font-size: 0.9em; }
.bdm-freeze-panel__message { margin-top: 6px; font-weight: 700; }
.bdm-freeze-panel__message--warning { color: #d97706; }
.bdm-freeze-panel__actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; }
.bdm-freeze-btn { display: inline-flex; align-items: center; gap: 6px; border: none; border-radius: 6px; padding: 6px 14px; font-size: var(--font-size-3xl); font-weight: 700; color: #ffffff; background: #0ea5e9; cursor: pointer; }
.bdm-freeze-btn:disabled { cursor: default; opacity: 0.7; }
.bdm-freeze-btn--ghost { background: transparent; color: inherit; border: 1px solid currentColor; }
.bdm-freeze-spin { display: inline-block; width: 10px; height: 10px; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: bdm-spin 0.8s linear infinite; }
.bdm-freeze-notice { margin: 0 0 16px; padding: 8px 12px; border-radius: 6px; font-size: var(--font-size-3xl); font-weight: 700; }
.bdm-freeze-notice--success { background: rgba(14, 165, 233, 0.12); color: #0ea5e9; }
.bdm-freeze-notice--warning { background: rgba(217, 119, 6, 0.12); color: #d97706; }
.bdm-freeze-notice--error { background: rgba(220, 38, 38, 0.12); color: #dc2626; }
.bdm-freeze-badge--overridden { opacity: 0.45; filter: grayscale(1); }
.bdm-odd-card__freeze-overridden { opacity: 0.45; filter: grayscale(1); margin-right: 2px; }
</style>
